<script>
import { mapState, mapActions } from 'vuex'

export default {
   data() {
      return {
         // b-tabs menggunakan index numerik sebagai nilai v-model.
         activeTab: 0,
         // Flag loading untuk menampilkan overlay spinner saat request berjalan.
         loading: false,
         fields: [
            { key: 'rank', label: '#', sortable: false },
            { key: 'jenis_perusahaan', label: 'Jenis Perusahaan', sortable: true },
            { key: 'total', label: 'Jumlah', sortable: true },
         ],
      }
   },
   computed: {
      ...mapState({
         nibPerJenis: state => state.DashboardOss.nibPerJenis,
         tahun: state => state.DashboardOss.tahun,
         refreshTimestamp: state => state.DashboardOss.refreshTimestamp,
         skin: state => state.appConfig.layout.skin,
      }),
      isDark() {
         return this.skin === 'dark'
      },
      activeTabName() {
         return this.activeTab === 0 ? 'tahun' : 'all_time'
      },
      filteredData() {
         // Data dari API sudah terfilter berdasarkan parameter tab di backend.
         return this.nibPerJenis.data || []
      },
      rankedData() {
         return this.filteredData
            .slice()
            .sort((a, b) => b.total - a.total)
            .map((item, index) => ({ ...item, rank: index + 1 }))
      },
      maxTotal() {
         if (!this.rankedData.length) return 100
         return Math.max(...this.rankedData.map(item => item.total))
      },
   },
   watch: {
      tahun(newYear) {
         this.loadData(newYear)
      },
      activeTab() {
         this.loadData(this.tahun)
      },
      refreshTimestamp() {
         this.loadData(this.tahun)
      },
   },
   mounted() {
      this.loadData(this.tahun)
   },
   methods: {
      ...mapActions({
         getNibPerJenis: 'DashboardOss/getNibPerJenis',
      }),
      async loadData(year) {
         this.loading = true
         try {
            await this.getNibPerJenis({ year, tab: this.activeTabName })
         } catch (error) {
            // Tabel tetap menampilkan data kosong saat request gagal.
         } finally {
            this.loading = false
         }
      },
      getProgressWidth(total) {
         return `${(total / this.maxTotal) * 100}%`
      },
      getProgressColor(index) {
         const colors = [
            '#9f1e15',
            '#28c76f',
            '#7367f0',
            '#ff9f43',
            '#00cfe8',
            '#ea5455',
            '#1d4ed8',
            '#8b5cf6',
            '#f59e0b',
            '#10b981',
         ]
         return colors[index % colors.length]
      },
   },
}
</script>

<template>
   <b-overlay
      :show="loading"
      :variant="isDark ? 'dark' : 'white'"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
   >
      <b-row>
         <b-col cols="12">
            <b-card class="card-wrapper">
               <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-1">
                  <div class="mb-1 mb-md-0">
                     <h4 class="mb-25">
                        NIB per Jenis Perusahaan
                     </h4>
                     <small class="text-muted">Distribusi NIB berdasarkan bentuk usaha</small>
                  </div>
               </div>

               <div class="mb-1">
                  <b-tabs v-model="activeTab" pills>
                     <b-tab :title="`Tahun ${tahun}`" name="tahun" />
                     <b-tab title="All Time" name="all_time" />
                  </b-tabs>
               </div>

               <!-- Desktop View - Table with Progress Bar -->
               <div class="d-none d-md-block">
                  <b-table
                     :items="rankedData"
                     :fields="fields"
                     :busy="false"
                     class="jenis-table"
                     responsive
                  >
                     <template #cell(rank)="data">
                        <div
                           class="rank-badge d-flex align-items-center justify-content-center font-weight-bolder"
                           :style="{
                              background: getProgressColor(data.index) + '20',
                              color: getProgressColor(data.index),
                           }"
                        >
                           {{ data.item.rank }}
                        </div>
                     </template>

                     <template #cell(jenis_perusahaan)="data">
                        <div class="text-wrap" style="max-width: 300px;">
                           {{ data.item.jenis_perusahaan }}
                        </div>
                     </template>

                     <template #cell(total)="data">
                        <div class="d-flex align-items-center">
                           <div class="progress-wrapper mr-1">
                              <div
                                 class="progress-bar-custom"
                                 :style="{
                                    width: getProgressWidth(data.item.total),
                                    background: getProgressColor(data.index),
                                 }"
                              />
                           </div>
                           <span class="font-weight-bolder">
                              {{ data.item.total.toLocaleString('id-ID') }}
                           </span>
                        </div>
                     </template>
                  </b-table>
               </div>

               <!-- Mobile View - Card List -->
               <div class="d-md-none">
                  <b-list-group>
                     <b-list-group-item
                        v-for="(item, index) in rankedData"
                        :key="item.jenis_perusahaan"
                        class="jenis-card-item"
                     >
                        <div class="d-flex justify-content-between align-items-start mb-1">
                           <div class="d-flex align-items-center">
                              <div
                                 class="rank-badge-sm mr-2 font-weight-bolder"
                                 :style="{
                                    background: getProgressColor(index) + '20',
                                    color: getProgressColor(index),
                                 }"
                              >
                                 {{ item.rank }}
                              </div>
                              <div>
                                 <div class="text-wrap" style="max-width: 180px;">
                                    {{ item.jenis_perusahaan }}
                                 </div>
                              </div>
                           </div>
                           <div
                              class="font-weight-bolder ml-2"
                              :style="{ color: getProgressColor(index) }"
                           >
                              {{ item.total.toLocaleString('id-ID') }}
                           </div>
                        </div>
                        <div
                           class="progress-bar-custom"
                           :style="{
                              width: getProgressWidth(item.total),
                              background: getProgressColor(index),
                           }"
                        />
                     </b-list-group-item>
                  </b-list-group>
               </div>
            </b-card>
         </b-col>
      </b-row>
   </b-overlay>
</template>

<style scoped>
.card-wrapper {
   border: 0;
   border-radius: 12px;
   box-shadow: 0 4px 24px rgba(34, 41, 47, 0.08);
}

.jenis-table :deep(.table) {
   margin-bottom: 0;
}

.jenis-table :deep(th) {
   border-top: none;
   font-size: 0.85rem;
   color: #6e6b7b;
}

.jenis-table :deep(td) {
   vertical-align: middle;
}

.rank-badge {
   width: 32px;
   height: 32px;
   border-radius: 8px;
   font-size: 0.875rem;
}

.rank-badge-sm {
   width: 28px;
   height: 28px;
   border-radius: 6px;
   font-size: 0.75rem;
   display: flex;
   align-items: center;
   justify-content: center;
}

.progress-wrapper {
   width: 100px;
   height: 8px;
   background: #ebe9f1;
   border-radius: 4px;
   overflow: hidden;
}

.progress-bar-custom {
   height: 100%;
   border-radius: 4px;
   transition: width 0.3s ease;
}

.jenis-card-item {
   border: none;
   border-bottom: 1px solid #ebe9f1;
   border-radius: 0 !important;
   padding: 0.75rem;
}

.jenis-card-item:last-child {
   border-bottom: none;
}
</style>
